const BASE = "mt-3 flex w-full flex-col";

const VARIANTS = {
  default: `${BASE} h-full gap-5`,
  narrow: `${BASE} mx-auto h-full max-w-3xl gap-5`,
  split: `${BASE} h-full gap-5`,
  bleed: `${BASE} min-h-0 flex-1`,
};

/**
 * Root wrapper of every admin page. Adds no horizontal padding: the admin
 * layout owns the page gutters.
 *
 * @param {object} props
 * @param {"default"|"narrow"|"split"|"bleed"} [props.variant="default"]
 *   `default`: full-width column, gap-5 between sections.
 *   `narrow`: same column capped at max-w-3xl and centered (single forms).
 *   `split`: `header` on top, then a side column (`aside`, w-56, sticky on md+) beside the content.
 *   `bleed`: fills the remaining height with no gap (maps, calendars).
 * @param {React.ReactNode} [props.header] Rendered first; typically a `PageHeader`.
 *   Optional for the other variants, where the header can simply be the first child.
 * @param {React.ReactNode} [props.aside] `split` only: side navigation column.
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 */
export default function PageContainer({
  variant = "default",
  header,
  aside,
  className = "",
  children,
}) {
  const classes = `${VARIANTS[variant] || VARIANTS.default} ${className}`.trim();

  if (variant === "split") {
    return (
      <div className={classes}>
        {header}
        <div className="flex flex-col gap-6 md:flex-row">
          {aside && (
            <aside className="md:sticky md:top-20 md:w-56 md:shrink-0 md:self-start">{aside}</aside>
          )}
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      {header}
      {children}
    </div>
  );
}
